import { A } from '@mobily/ts-belt'
import {
  api,
  LiveChannel,
  TimeRange,
  TvChannelWithEpg,
} from '@setplex/tria-api'
import { formatEPGPrograms } from '@setplex/tria-api-adapter-ovp/src/entities/live/format'
import { attach, createEffect, createEvent, sample, split } from 'effector'
import { NO_EPG_ID, type EpgRequestParams } from '../index.h'
import { isValidRange } from '../lib/ranges'

// event to guard calling actual fetch, in case of bad params
export const fetchEpgInfo = createEvent<EpgRequestParams>()

// actual fetch epg data effect
export const fetchEpgInfoFx = attach({
  effect: api.live.base.getEpgInformationFormattedFx,
})

// Analog of api.live.base.getEpgInformationFormattedFx with same formatting, but without request to BE
// (used for channels without epgIds)
export const generateEmptyProgramsFx = createEffect(
  ({
    epgIds,
    channels,
    period,
  }: {
    epgIds: string[]
    channels: LiveChannel[] | TvChannelWithEpg[]
    period: TimeRange
  }) => {
    if (!epgIds?.length) return { epg: {}, period } // no sense request // b.e. returns empty obj in that case

    return {
      epg: formatEPGPrograms({ 0: [] }, channels, period),
      period,
    }
  }
)

export const $isFetchEpgInfoFxPending = fetchEpgInfoFx.pending

// fetch epg data for channels for biggest calculated range
const fetchPrograms = createEvent<EpgRequestParams>()

const generateEmptyPrograms = createEvent<EpgRequestParams>()

split({
  source: fetchEpgInfo,
  match: {
    fetchPrograms: ({ channels, period }) => {
      const epgIds = A.uniq(
        A.map(channels, ({ channelEpgId }) => channelEpgId ?? NO_EPG_ID)
      ).filter((id) => id !== NO_EPG_ID)
      return epgIds.length > 0 && isValidRange(period)
    },
    generateEmptyPrograms: ({ channels, period }) => {
      const epgIds = A.uniq(
        A.map(channels, ({ channelEpgId }) => channelEpgId ?? NO_EPG_ID)
      ).filter((id) => id !== NO_EPG_ID)
      return channels.length > 0 && epgIds.length === 0 && isValidRange(period)
    },
  },
  cases: {
    fetchPrograms,
    generateEmptyPrograms,
  },
})

sample({
  clock: fetchPrograms,
  fn: ({ channels, period }) => {
    return {
      epgIds: A.uniq(
        A.map(channels, ({ channelEpgId }) => channelEpgId ?? NO_EPG_ID)
      ) as string[],
      channels,
      // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
      period: { start: period![0], end: period![1] },
    }
  },
  target: fetchEpgInfoFx,
})

sample({
  clock: generateEmptyPrograms,
  fn: ({ channels, period }) => {
    return {
      epgIds: A.uniq(
        A.map(channels, ({ channelEpgId }) => channelEpgId ?? NO_EPG_ID)
      ) as string[],
      channels,
      // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
      period: { start: period![0], end: period![1] },
    }
  },
  target: generateEmptyProgramsFx,
})
